"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ArrowRight, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  standardsBadge: string;
  h1: string;
  lead: string;
  viewTechnical: string;
  specStandard: string;
  specCustom: string;
  profileBadge: string;
  cardFootNote: string;
  selectionGuide: string;
  minSlotLabel: string;
  slotRangeLabel: string;
  flowDirectionLabel: string;
  flowDirectionVal: string;
  metallurgyLabel: string;
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Profile Wire Screens",
    standardsBadge: "ISO 9044 / ASTM E2016",
    h1: "Profile-wire screens for continuous-slot filtration, screening, dewatering and separation.",
    lead: "Custom profile wires resistance-welded to rigid support rods provide non-clogging open area, high mechanical strength, and reliable aperture control across water, petrochemical, and mining systems.",
    viewTechnical: "View technical details",
    specStandard: "ASTM E2016 standard compliance",
    specCustom: "Custom slot width tolerances",
    profileBadge: "Profile: V-Wire Welded",
    cardFootNote: "Cylinders, flat panels, sieve bends & baskets",
    selectionGuide: "Selection guide",
    minSlotLabel: "Min Slot Aperture",
    slotRangeLabel: "Slot Width Range",
    flowDirectionLabel: "Flow Direction",
    flowDirectionVal: "Out-to-In / In-to-Out",
    metallurgyLabel: "Standard Metallurgy"
  },
  zh: {
    categoryBadge: "精密型线焊接筛网",
    standardsBadge: "ISO 9044 / ASTM E2016",
    h1: "用于连续缝隙过滤、筛分、脱水与分离的精密型材丝筛网",
    lead: "高精度异型楔形丝与刚性支撑杆电阻熔焊而成，具备高开孔率、自防堵性能、卓越的机械强度与可靠的缝隙精度，广泛应用于水处理、石油化工及矿业分离系统。",
    viewTechnical: "查看技术规格",
    specStandard: "符合 ASTM E2016 规范",
    specCustom: "支持定制窄缝公差",
    profileBadge: "丝型：V型截面焊接丝",
    cardFootNote: "圆筒、平板、弧形筛与定制篮框",
    selectionGuide: "选型指南",
    minSlotLabel: "最小缝隙精度",
    slotRangeLabel: "标准缝隙范围",
    flowDirectionLabel: "流向设计",
    flowDirectionVal: "外进内出 / 内进外出",
    metallurgyLabel: "常用合金材质"
  },
  es: {
    categoryBadge: "Rejillas de alambre perfilado",
    standardsBadge: "ISO 9044 / ASTM E2016",
    h1: "Rejillas de alambre de cuña para filtración, tamizado, deshidratación y separación de ranura continua.",
    lead: "Alambres perfilados electrosoldados por resistencia a varillas de soporte rígidas ofrecen área abierta antiobstrucción, alta resistencia mecánica y control riguroso de abertura.",
    viewTechnical: "Ver detalles técnicos",
    specStandard: "Conforme con norma ASTM E2016",
    specCustom: "Tolerancias de ranura personalizadas",
    profileBadge: "Perfil: Alambre en V soldado",
    cardFootNote: "Cilindros, paneles planos, tamices curvos y cestas",
    selectionGuide: "Guía de selección",
    minSlotLabel: "Abertura mínima de ranura",
    slotRangeLabel: "Rango de ancho de ranura",
    flowDirectionLabel: "Dirección de flujo",
    flowDirectionVal: "Exterior a interior / Interior a exterior",
    metallurgyLabel: "Metalurgia estándar"
  },
  de: {
    categoryBadge: "Profil- und Spaltsiebe",
    standardsBadge: "ISO 9044 / ASTM E2016",
    h1: "Spaltsiebe mit Endlosspalt für Filtration, Siebung, Entwässerung und mechanische Trennung.",
    lead: "Auf starre Tragstäbe widerstandsgeschweißte V-Drähte gewährleisten verstopfungsfreie offene Siebflächen, hohe Druckfestigkeit und präzise Spaltweitenkontrolle.",
    viewTechnical: "Technische Daten ansehen",
    specStandard: "Konform nach ASTM E2016",
    specCustom: "Kundenspezifische Spaltweitentoleranzen",
    profileBadge: "Profil: V-Draht geschweißt",
    cardFootNote: "Zylinder, Flachsiebe, Bogensiebe & Körbe",
    selectionGuide: "Auswahlleitfaden",
    minSlotLabel: "Min. Spaltweite",
    slotRangeLabel: "Spaltweitenbereich",
    flowDirectionLabel: "Strömungsrichtung",
    flowDirectionVal: "Außen nach innen / Innen nach außen",
    metallurgyLabel: "Standardwerkstoffe"
  },
  fr: {
    categoryBadge: "Grilles à fils profilés",
    standardsBadge: "ISO 9044 / ASTM E2016",
    h1: "Grilles en fil profilé à fente continue pour la filtration, le criblage, l'essorage et la séparation.",
    lead: "Fils profilés en V soudés par résistance électrique sur des barres de support rigides assurant une surface ouverte anticolmatage, une haute résistance mécanique et une ouverture précise.",
    viewTechnical: "Voir les caractéristiques",
    specStandard: "Conforme à la norme ASTM E2016",
    specCustom: "Tolérances de fente sur mesure",
    profileBadge: "Profil : Fil en V soudé",
    cardFootNote: "Cylindres, panneaux plans, grilles courbes & paniers",
    selectionGuide: "Guide de sélection",
    minSlotLabel: "Fente minimale",
    slotRangeLabel: "Plage de largeur de fente",
    flowDirectionLabel: "Sens d'écoulement",
    flowDirectionVal: "Extérieur vers intérieur / Intérieur vers extérieur",
    metallurgyLabel: "Nuances métallurgiques standard"
  },
  pt: {
    categoryBadge: "Telas de arame perfilado",
    standardsBadge: "ISO 9044 / ASTM E2016",
    h1: "Telas de arame perfilado para filtração, peneiramento, desaguamento e separação de fenda contínua.",
    lead: "Arames perfilados eletrossoldados por resistência a barras de suporte rígidas garantem área aberta antientupimento, alta resistência mecânica e controle dimensional de fenda.",
    viewTechnical: "Ver detalhes técnicos",
    specStandard: "Conformidade com ASTM E2016",
    specCustom: "Tolerâncias de fenda sob medida",
    profileBadge: "Perfil: Arame em V soldado",
    cardFootNote: "Cilindros, painéis planos, telas curvas e cestos",
    selectionGuide: "Guia de seleção",
    minSlotLabel: "Abertura mínima de fenda",
    slotRangeLabel: "Faixa de abertura de fenda",
    flowDirectionLabel: "Direção do fluxo",
    flowDirectionVal: "Fora para dentro / Dentro para fora",
    metallurgyLabel: "Metalurgia padrão"
  }
};
export default function WedgeHero() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  const heroSpecs = [{
    label: c.minSlotLabel,
    value: "20 µm (0.02 mm)"
  }, {
    label: c.slotRangeLabel,
    value: "0.02 mm – 5.0 mm"
  }, {
    label: c.flowDirectionLabel,
    value: c.flowDirectionVal
  }, {
    label: c.metallurgyLabel,
    value: "AISI 304 / 316L / Duplex"
  }];
  return <section id="wedgewirehero" data-nav="light" className="relative overflow-hidden bg-background text-foreground mf-hero border-b border-border">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Column: Copy & Actions (7 cols) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Technical Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 text-sm font-mono tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" />
                {c.categoryBadge}
              </Badge>
              <Badge variant="outline" className="rounded-md border border-border bg-card px-2.5 py-1 text-sm font-mono text-foreground">
                {c.standardsBadge}
              </Badge>
            </div>

            {/* Headline */}
            <h1 className="mf-h1 font-bold tracking-tight text-foreground leading-[1.15]">
              {c.h1}
            </h1>

            {/* Lead */}
            <p className="mf-lead text-muted-foreground leading-relaxed max-w-2xl mt-4">
              {c.lead}
            </p>

            {/* Action buttons (max 2) */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="lg" className="px-6 font-semibold">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="px-6 font-semibold border-border bg-card hover:bg-muted text-foreground">
                <Link to="#wedgewirecategories" className="inline-flex items-center gap-2">
                  <span>{c.viewTechnical}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Micro spec text */}
            <div className="pt-4 border-t border-border w-full flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground font-mono mt-6">
              <span className="flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-chart-1" />
                {c.specStandard}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-chart-1" />
                {c.specCustom}
              </span>
            </div>
          </Reveal>

          {/* Right Column: Exactly ONE bordered Card (5 cols) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual */}
              <div className="relative mf-ratio-hero w-full mf-card-media">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_cylinder.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_cylinder.png-wvc-sizes" alt={lang === "zh" ? "精密不锈钢连续缝隙约翰逊楔形丝滤筒组件" : "Precision stainless steel continuous slot wedge wire screen element"} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" src="https://mutualfilter.com/wp-content/uploads/2026/09/wedge_wire-screen.webp" />
                <div className="absolute top-3 left-3 bg-surface-dark text-surface-dark-foreground px-2.5 py-1 rounded-md text-sm font-mono border border-surface-dark-border">
                  {c.profileBadge}
                </div>
              </div>

              {/* Single Footer Line */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <span className="size-1.5 rounded-full bg-chart-1 shrink-0" />
                    <span>{c.cardFootNote}</span>
                  </div>
                  <Link to="/WedgeWireScreenSelectionGuidePage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{c.selectionGuide}</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {heroSpecs.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}